import { isString, merge } from 'lodash-es';

import { SPLIT_CONTROL_TREATMENT } from '~coreServer/split/split-constants';

export const FEATURE_SOURCES = {
    queryString: 'queryString',
    cookie: 'cookie',
    split: 'split',
    contentful: 'contentful',
    envVar: 'envVar',
    defaultValue: 'defaultValue',
};

// The order of precedence for feature sources. Sources listed earlier take precedence over later ones.
const precedenceOrder = [
    FEATURE_SOURCES.queryString,
    FEATURE_SOURCES.cookie,
    FEATURE_SOURCES.split,
    FEATURE_SOURCES.contentful,
    FEATURE_SOURCES.envVar,
    FEATURE_SOURCES.defaultValue,
];

const isTruthy = v => [true, 'true', 1, '1', 'on'].indexOf(v) >= 0;
const isFalsy = v => [false, 'false', 0, '0', 'off'].indexOf(v) >= 0;

/**
 * Selects the first non-null string value from a map based on precedence order
 * @param {Object} map - Map of source keys to their string values
 * @returns {Object} Object with selected value and its source
 */
function pickFirstNonNullString(map) {
    const foundEntry = precedenceOrder.find(key => isString(map[key]));

    if (foundEntry) {
        return { source: foundEntry, value: map[foundEntry] };
    }

    return { source: FEATURE_SOURCES.defaultValue, value: map[FEATURE_SOURCES.defaultValue] };
}

/**
 * Selects the first non-null boolean value from a map based on precedence order
 * @param {Object} map - Map of source keys to their boolean values
 * @returns {Object} Object with selected value and its source
 */
function pickFirstNonNullBoolean(map) {
    const foundEntry = precedenceOrder.find(key => map[key] !== null && typeof map[key] === 'boolean');

    if (foundEntry) {
        return { source: foundEntry, value: map[foundEntry] };
    }

    return { source: FEATURE_SOURCES.defaultValue, value: map[FEATURE_SOURCES.defaultValue] };
}

/**
 * Selects the appropriate value picker function based on flag type
 * @param {Object} map - Map of source keys to their values
 * @param {boolean} isMultivariate - Whether the flag is multivariate (string) or boolean
 * @returns {Object} Object with selected value and its source
 */
export function pickFirstNonNullValueForFlagType(map, isMultivariate) {
    return isMultivariate ? pickFirstNonNullString(map) : pickFirstNonNullBoolean(map);
}

/**
 * Coerces a value to the appropriate type based on whether the flag is multivariate
 * @param {*} value - The value to coerce
 * @param {boolean} isMultivariate - Whether the flag is multivariate (string) or boolean
 * @returns {boolean|string|null} Coerced value (boolean for boolean flags, string for multivariate, or null if invalid)
 */
export function coerceValue(value, isMultivariate) {
    if (value === null || value === undefined || value === SPLIT_CONTROL_TREATMENT) return null;

    if (isMultivariate) return value;

    if (isTruthy(value)) { return true; }

    if (isFalsy(value)) { return false; }

    return null;
}

/**
 * Returns an object describing every possible source value for a feature flag
 * plus a quick-access `{ value, source }` pair chosen by precedence.
 *
 * @param {Object} request - The HTTP request object, containing cookies, query params, etc
 * @param {Object} cfg - Feature flag configuration object
 * @param {*} cfg.default - Default value for the feature flag
 * @param {string} [cfg.envVar] - Environment variable name to check for flag value
 * @param {Array<string>} [cfg.cookies=[]] - List of cookie names to check for flag value
 * @param {string} [cfg.queryString] - Query param name to check for flag value
 * @param {*} [cfg.contentfulValue] - Value from Contentful CMS
 * @param {*} [cfg.splitValue] - Value from Split.io
 * @param {boolean} [cfg.multivariate=false] - Whether this is a multivariate (string) flag
 * @param {boolean} [cfg.devOnly=false] - Whether this flag should configurable higher environments
 * @returns {Object} Object containing all source values and the selected value based on precedence
 */
export function getSourceValues(request, cfg) {
    const {
        default: defaultVal,
        envVar,
        cookies = [],
        queryString,
        contentfulValue,
        splitValue,
        multivariate = false,
        devOnly = false,
    } = cfg;

    // Do not allow devOnly features to be toggled in production environments, return the default value
    if (process.env.NODE_ENV === 'production' && devOnly) {
        return {
            values: {
                [FEATURE_SOURCES.queryString]: null,
                [FEATURE_SOURCES.cookie]: null,
                [FEATURE_SOURCES.split]: null,
                [FEATURE_SOURCES.contentful]: null,
                [FEATURE_SOURCES.envVar]: null,
                [FEATURE_SOURCES.defaultValue]: defaultVal,
            },
            value: defaultVal,
            source: FEATURE_SOURCES.defaultValue,
        };
    }

    const envValue = process.env[envVar] ?? null;
    const cookieValue = cookies.map(cookie => request.cookies?.[cookie])?.find?.(v => v);
    const qsValue = request.query?.[queryString] ?? null;

    const raw = {
        [FEATURE_SOURCES.queryString]: qsValue ?? null,
        [FEATURE_SOURCES.cookie]: cookieValue ?? null,
        [FEATURE_SOURCES.split]: splitValue ?? null,
        [FEATURE_SOURCES.contentful]: contentfulValue ?? null,
        [FEATURE_SOURCES.envVar]: envValue ?? null,
        [FEATURE_SOURCES.defaultValue]: defaultVal,
    };

    // For multivariate flags we do no coercion – leave strings intact.
    if (multivariate) {
        // 'control' is a special value from split which signifies that no treatment was able to be fetched
        // https://help.split.io/hc/en-us/articles/360020528072-Control-treatment
        if (raw[FEATURE_SOURCES.split] === SPLIT_CONTROL_TREATMENT) {
            raw[FEATURE_SOURCES.split] = null; // Treat as not present
        }

        const picked = pickFirstNonNullString(raw);
        return {
            values: raw,
            value: picked.value,
            source: picked.source,
        };
    }

    const normalised = { ...raw };
    // convert each entry to true|false|null.
    Object.entries(normalised).forEach(([key, v]) => {
        if (v === null || v === undefined) {
            normalised[key] = null;
            return;
        }

        if (key === FEATURE_SOURCES.split && v === SPLIT_CONTROL_TREATMENT) {
            normalised[key] = null; // treat as not set
            return;
        }

        if (isTruthy(v)) {
            normalised[key] = true;
        } else if (isFalsy(v)) {
            normalised[key] = false;
        } else {
            normalised[key] = null;
        }
    });

    const picked = pickFirstNonNullBoolean(normalised);

    return {
        values: normalised,
        value: picked.value,
        source: picked.source,
    };
}

/**
 * Builds the initial feature values map by processing all feature configs
 *
 * @param {Object} request - The HTTP request object
 * @param {Object} featureConfigs - Object mapping feature IDs to their configurations
 * @param {Object} logger - Logger instance
 * @returns {Object} Object mapping feature IDs to their resolved values and sources
 */
export function buildInitialFeatureValues(request, featureConfigs, logger) {
    const featureObjects = Object.keys(featureConfigs).reduce((acc, id) => {
        acc[id] = getSourceValues(request, featureConfigs[id]);
        return acc;
    }, {});

    const simplified = Object.fromEntries(
        Object.entries(featureObjects).map(([id, obj]) => [id, obj.value]),
    );
    logger.debug('Resolved initial feature values: ', simplified);

    return featureObjects;
}

/**
 * Returns merged feature configurations combining default and app-specific configs
 *
 * @param {Object} appFeatureConfigs - The application-specific feature configurations
 * @returns {Object} The merged feature configurations object containing both global and app-specific configs
 */
export function getFeatureConfigs(appFeatureConfigs) {
    const defaultFeatureConfigs = {
        brazeSdk: {
            default: false,
            multivariate: false,
            envVar: 'NUU_BRAZE_SDK_ENABLED',
            cookies: [
                'nuu-braze-sdk-enabled',
            ],
            queryString: 'nuu-braze-sdk-enabled',
        },
        disableCsp: {
            default: false,
            devOnly: true, // Do not allow usage in prod builds
            envVar: 'NUU_DISABLE_CSP',
            cookies: [
                'nuu-disable-csp',
            ],
            queryString: 'nuu-disable-csp',
        },
        googleTagManager: {
            default: true,
            multivariate: false,
            envVar: 'NUU_GTM_ENABLED',
            cookies: [
                'nuu-gtm-enabled',
            ],
            queryString: 'nuu-gtm-enabled',
        },
        newRelic: {
            default: false,
            multivariate: false,
            envVar: 'NUU_NEW_RELIC_ENABLED',
            cookies: [
                'nuu-new-relic-enabled',
            ],
            queryString: 'nuu-new-relic-enabled',
        },
        // NU_FEATURE: CONTENT_CARDS
        // https://urbnit.atlassian.net/browse/TYP-25489
        contentCards: {
            default: true,
            multivariate: false,
            envVar: 'NUU_ENABLE_CONTENT_CARDS',
            cookies: [
                'nuu-enable-content-cards',
            ],
            queryString: 'nuu-enable-content-cards',
        },
        useSplit: {
            default: false,
            multivariate: false,
            envVar: 'NUU_USE_SPLIT_CLIENT',
            cookies: [
                'nuu-use-split-client',
            ],
            queryString: 'nuu-use-split-client',
        },
    };

    const featureConfigs = merge(defaultFeatureConfigs, appFeatureConfigs);

    return featureConfigs;
}
